import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccountModules } from '../../services/accountModulesService';
import { SECTION_GROUPS, isTabExact, visibleTabs } from './navigation';

// Abas no topo da página com as telas irmãs do assunto aberto na barra lateral.
// Só aparecem nas telas de lista (não dentro de uma campanha/concorrente aberto).
const SectionTabs: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const modules = useAccountModules();
  const activeRef = useRef<HTMLAnchorElement | null>(null);

  let tabs = null as ReturnType<typeof visibleTabs> | null;
  for (const g of SECTION_GROUPS) {
    const list = visibleTabs(g, modules);
    if (list.some((tab) => isTabExact(tab, pathname))) { tabs = list; break; }
  }

  // No celular a barra rola de lado: deixa a aba atual sempre à vista
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [pathname]);

  if (!tabs || tabs.length < 2) return null;

  return (
    <div className="relative mb-4 sm:mb-6 -mt-1">
      <nav
        aria-label={t('nav.sections.tabsAria', 'Telas desta seção')}
        className="flex gap-1 overflow-x-auto overscroll-x-contain border-b border-gray-200 dark:border-gray-700 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => {
          const active = isTabExact(tab, pathname);
          const Icon = tab.icon;
          return (
            <Link
              key={tab.href}
              to={tab.href}
              ref={active ? activeRef : undefined}
              aria-current={active ? 'page' : undefined}
              className={`relative shrink-0 inline-flex items-center gap-2 px-3 sm:px-4 pt-2 pb-3 text-sm whitespace-nowrap rounded-t-lg transition-colors ${
                active
                  ? 'text-primary-700 dark:text-primary-300 font-semibold'
                  : 'text-gray-500 dark:text-gray-400 font-medium hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/70 dark:hover:bg-gray-800/70'
              }`}
            >
              <Icon className={`h-4 w-4 ${active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
              {t(tab.key, tab.fallback)}
              {active && <span className="absolute inset-x-2 -bottom-px h-[3px] rounded-full bg-primary-600 dark:bg-primary-400" />}
            </Link>
          );
        })}
      </nav>
      {/* dica de que tem mais abas para o lado (celular) */}
      <div className="pointer-events-none absolute right-0 top-0 bottom-px w-8 bg-gradient-to-l from-gray-50 dark:from-gray-900 to-transparent sm:hidden" />
    </div>
  );
};

export default SectionTabs;
